<template>
  <SelectTagButton
    ref="targetRef"
    role="list"
    :bs="bs"
    :is-open="isActive"
    :filter-text="filterText"
    :items="selectedItems"
    :input-mode="searchAttrs ? 'select' : inputMode"
    :readonly="readonly"
    :disabled="disabled"
    :remove-item="handleSelect"
    v-bind="$attrs"
    @input="handleInput"
    @click.prevent="() => toggle()"
  >
    <template #left>
      <!-- @slot 按钮左方区域，常用来自定义图标 -->
      <slot name="left" />
    </template>
    <BodyPortal :active="fixed">
      <SelectBox
        ref="popperRef"
        :input-mode="inputMode"
        :status="statusHook"
        :filter-text="filterText"
        :allow-custom="allowCustom"
        :add-option="addOption"
        :empty="items.length < 1"
        :bs="popperBs"
        @click.stop="() => {}"
      >
        <template
          v-if="searchAttrs"
          #head
        >
          <NewBsBox :bs="{ px: 12, mb: 8 }">
            <BeerInput
              :bs="{ width: '100%' }"
              :value="filterText"
              auto-focus
              v-bind="searchAttrs"
              @input="handleInput"
            >
              <template #left>
                <BeerIcon
                  icon="search"
                  :size="14"
                  :bs="{ ml: 11 }"
                  color="mute"
                />
              </template>
            </BeerInput>

          </NewBsBox>
        </template>
        <div>
          <!--
          @slot 选项列表，可以自定义选项的样式
          @binding {func} handleSelect 用来触发选中
          @binding {ItemType[]} items  当前状态的计算 items 列表
          @binding {string[]} activeIds 当前选中的 items 计算 id
        -->
          <slot
            :items="items"
            :handleSelect="handleSelect"
            :activeIds="activeIds"
          >
            <Option
              v-for="o of items"
              :key="o.id"
              :color-svg="false"
              :bs="{ py: 0 }"
            >
              <StatelessCheck
                :bs="{
                  display: 'flex',
                  mr: 0,
                }"
                :label="o.text"
                :model-value="activeIds.indexOf(o.id) > -1"
                :handle-change="() => handleSelect(o)"
                :disabled="o.raw?.disabled || (reachMax && activeIds.indexOf(o.id) < 0)"
              />
              <template
                v-if="o.isCustom"
                #right
              >
                <BeerIcon
                  title="自定义"
                  icon="setting"
                />
              </template>
            </Option>
          </slot>
        </div>
        <template
          #action
        >
          <BeerButton
            v-if="allowAll"
            :bs="{ mr: 'auto', ml: 8 }"
            :handle-click="handleSelectAll"
            variant="stroke"
            aria-label="all"
            modifier="danger"
            size="s"
          >
            全选
          </BeerButton>
          <BeerButton
            :bs="{ mr: 0, minWidth: 'unset'}"
            modifier="body"
            variant="text"
            aria-label="clear"
            size="s"
            @click="handleClear"
          >
            清空
          </BeerButton>
        </template>
      </SelectBox>
    </BodyPortal>
  </SelectTagButton>
</template>

<script lang="ts">
  import {
    computed, PropType, defineComponent,
  } from 'vue'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import Option from '@xhs/yam-beer/cmp/overlayers/Option/Option.vue'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import StatelessCheck from '@xhs/yam-beer/cmp/dataEntry/Checkbox/StatelessCheckbox.vue'
  import { BeerStyleObj } from '@xhs/yam-beer/core'
  import SelectTagButton from './SelectTagButton.vue'
  import SelectBox from './SelectBox.vue'
  import useOptions, {
    useSelectListValue, ExtendItem, ItemType,
    UseOptionsParams,
    GetListFn,
    defaultFilter, PossbileValue,
  } from '../../../compose/options/useOptions'
  import { useSelectPopover, SelectPopoverPartial } from '../../../compose/popover'
  import BodyPortal from '../../primitive/BodyPortal'
  import BeerInput from '../Input/Input.vue'

  /**
   * 多选控件
   *
   * 其中的选项是 @xhs/yam-beer/cmp/overlayers/CheckboxOption， 可以传递对应的 props
   * - 如 { name: '选项A', value: 'A', binds: { disabled: true }}
   *
   * @model modelValue|@update:modelValue
   */
  export default defineComponent({
    name: 'MultiSelect',
    components: {
      BeerIcon,
      BeerButton,
      Option,
      StatelessCheck,
      SelectTagButton,
      SelectBox,
      BodyPortal,
      BeerInput,
      NewBsBox,
    },
    inheritAttrs: false,
    props: {
      /**
       * 固定选项列表
       */
      options: {
        type: Array as PropType<ItemType[]>,
        default: (): ItemType[] => ([]),
      },
      /**
       * 选项需要从方法中调用
       * - 配合 filterable 属性可以根据筛选值来搜索
       */
      getList: {
        type: Function as PropType<GetListFn>,
        required: false,
      },
      /**
       * 最多选的最大数
       */
      maxSize: {
        type: Number,
        required: false,
      },
      /**
       * 允许全选
       */
      allowAll: {
        type: Boolean,
        default: false,
      },
      /**
       * 选项值
       */
      modelValue: {
        type: Array as PropType<PossbileValue[]>,
      },
      /**
       * 输入通用属性，是否只读
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 输入通用属性，是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否选项列表找不到 modelvalue 的时候自动生成
       */
      addIfMissing: {
        type: Boolean,
        default: true,
      },
      /**
       * 选项按钮的样式
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 是否允许自定义选项
       */
      allowCustom: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否可以筛选选项
       */
      filterable: {
        type: Boolean,
        default: false,
      },
      /**
       * useOption 的自定义选项
       */
      optionParams: {
        type: Object as PropType<UseOptionsParams>,
        default: () => ({}),
      },
      /**
       * 下拉框是否是 fixed 模式
       */
      fixed: {
        type: Boolean,
        default: false,
      },
      /**
       * 下拉框的高级选项
       */
      popoverParams: {
        type: Object as PropType<SelectPopoverPartial>,
        default: () => ({}),
      },
      /**
       * popperBs
      */
      popperBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 提供了这个就提示搜索输入框在 popper 里面
       * 并提供 attrs 如 placehoder 的绑定
       */
      searchAttrs: {
        type: Object,
        required: false,
      },
    },
    emits: ['update:modelValue', 'reach-max'],
    setup(props, { emit }) {
      const optionsHook = useOptions({
        options: computed(() => props.options),
        getList: props.getList || undefined,
        filterFn: (props.filterable || !!props.searchAttrs)
          ? defaultFilter
          : undefined,
        ...props.optionParams,
      })
      const {
        targetRef,
        popperRef,
        isActive,
        update,
        toggle,
      } = useSelectPopover({
        strategy: props.fixed ? 'fixed' : 'absolute',
        ...props.popoverParams,
        disabled: computed(() => props.disabled || props.readonly),
      })

      const {
        selectedItems,
        activeIds,
        toggleItem,
      } = useSelectListValue(
        optionsHook.extItems,
        computed(() => props.modelValue || []),
        optionsHook,
        props.addIfMissing,
      )

      const reachMax = computed(() => {
        if (
          typeof props.maxSize === 'number'
          && activeIds.value.length >= props.maxSize
        ) {
          return true
        }
        return false
      })

      const handleSelect = (o: ExtendItem) => {
        if (typeof o.raw !== 'string' && o.raw?.disabled) {
          return
        }
        if (
          reachMax.value
          && activeIds.value.indexOf(o.id) < 0
        ) {
          return
        }
        const valueList = toggleItem(o)
        if (valueList !== null) {
          emit('update:modelValue', valueList)
          if (reachMax.value) {
            emit('reach-max')
          }
        }
        update()
      }
      const handleClear = () => {
        optionsHook.filterText.value = ''
        emit('update:modelValue', [])
        update()
      }
      const handleSelectAll = () => {
        emit(
          'update:modelValue',
          optionsHook.items.value.slice(
            0,
            props.maxSize || optionsHook.items.value.length,
          ).map(i => i.value),
        )
        update()
      }

      let addOption
      if (optionsHook.addOption) {
        addOption = (text: string) => {
          optionsHook.filterText.value = ''
          optionsHook.addOption?.(text)
        }
      }

      return {
        handleInput(e: KeyboardEvent) {
          optionsHook.filterText.value = (e.target as HTMLInputElement).value?.trim()
          if (!isActive.value) {
            toggle(true)
          }
        },
        filterText: optionsHook.filterText,
        inputMode: optionsHook.inputMode,
        statusHook: optionsHook.statusHook,
        items: optionsHook.items,
        addOption,
        activeIds,
        selectedItems,
        isActive,
        targetRef,
        popperRef,
        toggle,
        handleSelect,
        handleClear,
        handleSelectAll,
        reachMax,
      }
    },
  })
</script>
